import { Injectable, Provider, Signal } from '@angular/core';
import { ApiGender } from '../api-types/gender.api';
import { Gender } from '../app-types/gender.app';
import { GENDER_API } from '../injection-tokens/services.token';
import { transformApiGendersToAppGenders } from '../transformers/gender.transform';
import { BaseRequestService } from './base-request.service';

/**
 * Service to interact with the Gender API.
 * This service provides methods to fetch and manipulate gender data.
 * It extends the BaseRequestService to handle API requests.
 *
 * Please do not provide this service directly in components. Use the corresponding
 * injection token `GENDER_API` to inject the service.
 */
@Injectable()
export class GenderApi extends BaseRequestService implements GenderApiProvider {
	/**
	 * Fetch all genders from the API.
	 * The API data is transformed to the application-specific
	 * format before being returned.
	 * @returns An array of genders.
	 */
	public getAll = async () => {
		const apiGenders = await this.get<ApiGender[]>('genders');
		const genders = transformApiGendersToAppGenders(apiGenders);
		return genders;
	};
}

/**
 * Interface for Gender API. Can also be used to mock the API in tests or for local development.
 */
export interface GenderApiProvider extends BaseRequestService {
	getAll: () => Promise<Gender[]>;
	isLoading: Signal<boolean>;
}

@Injectable()
export class GenderApiMock extends BaseRequestService implements GenderApiProvider {
	public constructor() {
		super();
		console.warn('⛔️⛔️⛔️ Gender Mock Service: You are currently using a mock service!');
	}

	public getAll = async () => {
		const genders: Gender[] = [
			{
				id: 1,
				initials: 'M',
				salutation: 'männlich',
				salutationSuggestion: 'Herr',
			},
			{
				id: 2,
				initials: 'F',
				salutation: 'weiblich',
				salutationSuggestion: 'Frau',
			},
			{
				id: 3,
				initials: 'NB',
				salutation: 'nicht-binär',
				salutationSuggestion: 'Diverse',
			},
		];
		return Promise.resolve(genders);
	};
}

export const provideGenderApi: Provider = {
	provide: GENDER_API,
	useClass: GenderApi,
};
